'use client';
import React, { useState } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { useCookieConsent } from '@/hooks/useCookieConsent';

const CookieConsent = () => {
  const {
    consent,
    hasConsent,
    updateConsent,
    showBanner,
    acceptAll,
    rejectAll,
    savePreferences,
  } = useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);

  if (!showBanner) return null;

  return (
    <m.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-lg z-50 p-4 md:p-6"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              We value your privacy
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.
              By clicking "Accept All", you consent to our use of cookies.
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="ml-1 text-orange-700 dark:text-blue-400 hover:underline focus:outline-none"
              >
                {showDetails ? 'Hide details' : 'Customize settings'}
              </button>
            </p>

            <AnimatePresence>
              {showDetails && (
                <m.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-4 space-y-4 overflow-hidden"
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">Necessary</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Essential for the website to function properly
                        </p>
                      </div>
                      <div className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-orange-800 dark:bg-blue-900/30 dark:text-blue-400">
                        Always active
                      </div>
                    </div>

                    {Object.entries({
                      preferences: 'Preferences',
                      analytics: 'Analytics',
                      marketing: 'Marketing',
                    }).map(([key, label]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">{label}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {key === 'preferences' && 'Remember your preferences'}
                            {key === 'analytics' && 'Help us understand how visitors interact'}
                            {key === 'marketing' && 'Show personalized content and ads'}
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={consent[key as keyof typeof consent]}
                            onChange={(e) => updateConsent({ [key]: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-orange-700"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </m.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {!showDetails ? (
              <>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-700 rounded-lg hover:bg-orange-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Accept All
                </button>
                <button
                  onClick={() => setShowDetails(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Customize
                </button>
              </>
            ) : (
              <button
                onClick={savePreferences}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-700 rounded-lg hover:bg-orange-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Save Preferences
              </button>
            )}
          </div>
        </div>
      </div>
    </m.div>
  );
};

export default CookieConsent;
